import { log } from '../utils/logger';

/**
 * Teams API module
 * Handles all team-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Team API namespace
 */
export const teamAPI = {
  /**
   * Create a new team
   * @param {Object} data - Team data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    (data) => db.createTeam(data),
    { operation: 'Create team' }
  ),

  /**
   * Update an existing team
   * @param {string} id - Team ID
   * @param {Object} data - Updated team data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    (id, data) => db.updateTeam(id, data),
    { operation: 'Update team' }
  ),

  /**
   * Delete a team
   * @param {string} id - Team ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      return await supabase
        .from('teams')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete team' }
  ),

  /**
   * Get a single team by ID
   * @param {string} id - Team ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('teams')
        .select('*')
        .eq('id', id)
        .single();
    },
    { operation: 'Get team' }
  ),

  /**
   * Get all teams with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('teams')
        .select('*');

      // Apply filters
      if (filters.managerId) {
        query = query.eq('manager_id', filters.managerId);
      }
      if (filters.division) {
        query = query.eq('division', filters.division);
      }
      if (filters.state) {
        query = query.eq('state', filters.state);
      }
      if (filters.sportActivity) {
        query = query.eq('sport_activity', filters.sportActivity);
      }
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
      }

      // Add ordering
      query = query.order('name', { ascending: true });

      return await query;
    },
    { operation: 'Get teams' }
  ),

  /**
   * Get total count of teams with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getCount: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('teams')
        .select('*', { count: 'exact', head: true });

      // Apply same filters as getAll
      if (filters.managerId) {
        query = query.eq('manager_id', filters.managerId);
      }
      if (filters.division) {
        query = query.eq('division', filters.division);
      }
      if (filters.state) {
        query = query.eq('state', filters.state);
      }
      if (filters.sportActivity) {
        query = query.eq('sport_activity', filters.sportActivity);
      }
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
      }

      const result = await query;
      return {
        data: null,
        count: result.count,
        error: result.error
      };
    },
    { operation: 'Get teams count' }
  ),

  /**
   * Get teams managed by a specific user
   * @param {string} userId - Manager user ID
   * @returns {Promise<APIResponse>}
   */
  getByManager: createAPIMethod(
    async (userId) => {
      return await db.getUserManagedTeams(userId);
    },
    { operation: 'Get user managed teams' }
  ),

  /**
   * Get team statistics
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getStatistics: createAPIMethod(
    async (teamId) => {
      // Get various statistics for the team
      const [
        reservationsResult,
        gamesResult,
        tournamentsResult
      ] = await Promise.allSettled([
        supabase
          .from('reservations')
          .select('status')
          .eq('team_id', teamId),
        supabase
          .from('games')
          .select('id, starts_at')
          .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`),
        supabase
          .from('tournament_teams')
          .select(`
            tournament_id,
            tournaments (
              name, start_date, end_date
            )
          `)
          .eq('team_id', teamId)
      ]);

      const reservations = reservationsResult.status === 'fulfilled' 
        ? reservationsResult.value.data || []
        : [];
      const games = gamesResult.status === 'fulfilled' 
        ? gamesResult.value.data || []
        : [];
      const tournaments = tournamentsResult.status === 'fulfilled' 
        ? tournamentsResult.value.data || []
        : [];

      // Process reservations by status
      const reservationStats = {
        total: reservations.length,
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
      };

      reservations.forEach(r => {
        if (reservationStats[r.status] !== undefined) {
          reservationStats[r.status]++;
        }
      });

      // Process games
      const now = new Date();
      const gameStats = {
        total: games.length,
        upcoming: games.filter(g => new Date(g.starts_at) > now).length,
        completed: games.filter(g => new Date(g.starts_at) <= now).length
      };

      return {
        reservations: reservationStats,
        games: gameStats,
        tournaments: {
          total: tournaments.length,
          active: tournaments.filter(t => {
            const startDate = new Date(t.tournaments.start_date);
            const endDate = new Date(t.tournaments.end_date);
            return startDate <= now && endDate >= now;
          }).length
        }
      };
    },
    { operation: 'Get team statistics' }
  ),

  /**
   * Add a team to a tournament
   * @param {string} tournamentId - Tournament ID
   * @param {string} teamId - Team ID
   * @param {string} managerId - Manager user ID
   * @returns {Promise<APIResponse>}
   */
  addToTournament: createAPIMethod(
    async (tournamentId, teamId, managerId) => {
      return await db.addTeamToTournament(tournamentId, teamId, managerId);
    },
    { operation: 'Add team to tournament' }
  ),

  /**
   * Remove a team from a tournament
   * @param {string} tournamentId - Tournament ID
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  removeFromTournament: createAPIMethod(
    async (tournamentId, teamId) => {
      return await supabase
        .from('tournament_teams')
        .delete()
        .eq('tournament_id', tournamentId)
        .eq('team_id', teamId);
    },
    { operation: 'Remove team from tournament' }
  ),

  /**
   * Get team tournaments
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getTournaments: createAPIMethod(
    async (teamId) => {
      return await db.getTeamTournamentsWithGames(teamId);
    },
    { operation: 'Get team tournaments' }
  ),

  /**
   * Get team games
   * @param {string} teamId - Team ID
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getGames: createAPIMethod(
    async (teamId, filters = {}) => {
      let query = supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, start_date, end_date, timezone
          ),
          home_team:teams!home_team_id (
            id, name
          ),
          away_team:teams!away_team_id (
            id, name
          ),
          game_locations (
            id, name, address_line1, city, state
          )
        `)
        .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`);

      // Apply filters
      if (filters.tournamentId) {
        query = query.eq('tournament_id', filters.tournamentId);
      }
      if (filters.upcoming) {
        query = query.gte('starts_at', new Date().toISOString());
      }
      if (filters.dateFrom) {
        query = query.gte('starts_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('starts_at', filters.dateTo);
      }

      // Add ordering
      query = query.order('starts_at', { ascending: true });

      return await query;
    },
    { operation: 'Get team games' }
  ),

  /**
   * Get team reservations
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getReservations: createAPIMethod(
    async (teamId) => {
      return await supabase
        .from('reservations')
        .select(`
          *,
          venues (
            id, name, address_line1, address_line2,
            city, state, zip_code, phone
          ),
          games (
            id, starts_at, ends_at,
            tournaments (
              id, name
            )
          )
        `)
        .eq('team_id', teamId)
        .order('reserved_at', { ascending: false });
    },
    { operation: 'Get team reservations' }
  ),

  /**
   * Search available teams for tournament registration
   * @param {string} searchTerm - Search term
   * @param {Array} excludeTeamIds - Team IDs to exclude
   * @returns {Promise<APIResponse>}
   */
  searchAvailable: createAPIMethod(
    async (searchTerm = '', excludeTeamIds = []) => {
      let query = supabase
        .from('teams')
        .select('*');

      // Exclude already registered teams
      if (excludeTeamIds.length > 0) {
        query = query.not('id', 'in', `(${excludeTeamIds.join(',')})`);
      }

      // Apply search filter
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`);
      }

      query = query.order('name').limit(20);

      return await query;
    },
    { operation: 'Search available teams' }
  ),

  /**
   * Get team divisions (unique values)
   * @returns {Promise<APIResponse>}
   */
  getDivisions: createAPIMethod(
    async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('division')
        .not('division', 'is', null);

      if (error) throw error;

      // Get unique divisions
      const divisions = [...new Set(data.map(t => t.division))].filter(Boolean).sort();

      return divisions;
    },
    { operation: 'Get team divisions' }
  ),

  /**
   * Get team states (unique values)
   * @returns {Promise<APIResponse>}
   */
  getStates: createAPIMethod(
    async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('state')
        .not('state', 'is', null);

      if (error) throw error;

      // Get unique states
      const states = [...new Set(data.map(t => t.state))].filter(Boolean).sort();

      return states;
    },
    { operation: 'Get team states' }
  ),

  /**
   * Update team manager
   * @param {string} teamId - Team ID
   * @param {string} newManagerId - New manager user ID
   * @returns {Promise<APIResponse>}
   */
  updateManager: createAPIMethod(
    async (teamId, newManagerId) => {
      return await supabase
        .from('teams')
        .update({
          manager_id: newManagerId,
          updated_at: new Date().toISOString()
        })
        .eq('id', teamId)
        .select()
        .single();
    },
    { operation: 'Update team manager' }
  ),

  /**
   * Get team access requests
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getAccessRequests: createAPIMethod(
    async (teamId) => {
      return await supabase
        .from('pending_team_managers')
        .select(`
          *,
          users (
            id, first_name, last_name, email, phone
          )
        `)
        .eq('team_id', teamId)
        .order('requested_at', { ascending: false });
    },
    { operation: 'Get team access requests' }
  ),

  /**
   * Advanced team search with multi-word support and follow-up searches
   * @param {string} searchQuery - Search query
   * @param {Object} options - Search options
   * @returns {Promise<APIResponse>}
   */
  advancedSearch: createAPIMethod(
    async (searchQuery, options = {}) => {
      const {
        includeUserRoles = false,
        excludeUserId = null,
        limit = 1000,
        maxResults = 30,
        state = null,
        division = null
      } = options;

      if (!searchQuery?.trim()) {
        return { data: [], count: 0 };
      }

      // Split query into individual words for flexible matching
      const searchWords = searchQuery.trim().toLowerCase().split(/\s+/).filter(word => word.length > 0);

      // Base query setup
      const selectFields = includeUserRoles
        ? `*, user_team_roles (user_id, role)`
        : 'id, name, sport_activity, description, state, division';

      let teamsQuery = supabase
        .from('teams')
        .select(selectFields)
        .eq('is_active', true);

      // Apply state filter if provided
      if (state) {
        teamsQuery = teamsQuery.eq('state', state);
      }

      // Apply division filter if provided
      if (division) {
        teamsQuery = teamsQuery.eq('division', division);
      }
      
      // Search strategy based on number of words
      if (searchWords.length === 1) {
        // Single word - simple substring search across all fields
        const word = searchWords[0];
        teamsQuery = teamsQuery.or(
          `name.ilike.%${word}%,description.ilike.%${word}%,sport_activity.ilike.%${word}%,state.ilike.%${word}%,division.ilike.%${word}%`
        );
      } else {
        // Multiple words - search for first and last word (most distinctive)
        const primaryWord = searchWords[0];
        const secondaryWord = searchWords[searchWords.length - 1];
        
        // Build query to find teams containing the primary word and optionally the last word
        let orConditions = `name.ilike.%${primaryWord}%,description.ilike.%${primaryWord}%,sport_activity.ilike.%${primaryWord}%`;
        
        if (secondaryWord && secondaryWord !== primaryWord) {
          // Also search for the last word (often the most specific, like "B1")
          orConditions += `,name.ilike.%${secondaryWord}%,division.ilike.%${secondaryWord}%`;
        }
        
        teamsQuery = teamsQuery.or(orConditions);
      }
      
      // Execute initial search
      const { data: teams, error: teamsError } = await teamsQuery
        .order('name')
        .limit(limit);

      if (teamsError) throw teamsError;

      log.info(`Advanced team search - Teams found before filtering: ${teams?.length || 0}`);

      // If we hit the limit and have multiple words, do additional searches to capture more results
      let allTeams = teams || [];
      if (searchWords.length > 1 && allTeams.length === limit) {
        log.info('Hit team limit, doing additional searches for better coverage...');
        
        // Search for other word combinations we might have missed
        const additionalSearches = [];
        
        // If we have 3+ words, search for middle words too
        if (searchWords.length >= 3) {
          for (let i = 1; i < searchWords.length - 1; i++) {
            const middleWord = searchWords[i];
            let middleWordQuery = supabase
              .from('teams')
              .select(selectFields)
              .eq('is_active', true);

            // Apply filters
            if (state) middleWordQuery = middleWordQuery.eq('state', state);
            if (division) middleWordQuery = middleWordQuery.eq('division', division);

            middleWordQuery = middleWordQuery
              .or(`name.ilike.%${middleWord}%,description.ilike.%${middleWord}%,sport_activity.ilike.%${middleWord}%,state.ilike.%${middleWord}%,division.ilike.%${middleWord}%`)
              .order('name')
              .limit(300);

            additionalSearches.push(middleWordQuery);
          }
        }
        
        // Do more targeted searches to find teams we might have missed
        if (searchWords.length >= 2) {
          const firstWord = searchWords[0];
          const lastWord = searchWords[searchWords.length - 1];

          // Strategy 1: Look for teams where both words appear in non-name fields
          let strategy1Query = supabase
            .from('teams')
            .select(selectFields)
            .eq('is_active', true);

          if (state) strategy1Query = strategy1Query.eq('state', state);
          if (division) strategy1Query = strategy1Query.eq('division', division);

          strategy1Query = strategy1Query
            .or(`description.ilike.%${firstWord}%,description.ilike.%${lastWord}%,division.ilike.%${firstWord}%,division.ilike.%${lastWord}%`)
            .order('name')
            .limit(300);

          additionalSearches.push(strategy1Query);

          // Strategy 2: Search for exact phrases or close combinations
          if (firstWord !== lastWord) {
            const phraseSearch = `${firstWord} ${lastWord}`;
            let strategy2Query = supabase
              .from('teams')
              .select(selectFields)
              .eq('is_active', true);

            if (state) strategy2Query = strategy2Query.eq('state', state);
            if (division) strategy2Query = strategy2Query.eq('division', division);

            strategy2Query = strategy2Query
              .or(`name.ilike.%${phraseSearch}%,description.ilike.%${phraseSearch}%`)
              .order('name')
              .limit(200);

            additionalSearches.push(strategy2Query);
          }

          // Strategy 3: Search with different ordering (by description instead of name)
          // This might catch teams that were cut off in the name-ordered search
          let strategy3Query = supabase
            .from('teams')
            .select(selectFields)
            .eq('is_active', true);

          if (state) strategy3Query = strategy3Query.eq('state', state);
          if (division) strategy3Query = strategy3Query.eq('division', division);

          strategy3Query = strategy3Query
            .or(`name.ilike.%${firstWord}%,name.ilike.%${lastWord}%`)
            .order('description')
            .limit(200);

          additionalSearches.push(strategy3Query);
        }
        
        // Execute additional searches in parallel
        if (additionalSearches.length > 0) {
          try {
            const additionalResults = await Promise.all(additionalSearches);
            const additionalTeams = additionalResults
              .filter(result => !result.error)
              .flatMap(result => result.data || []);
            
            log.info(`Found ${additionalTeams.length} additional teams from ${additionalSearches.length} searches`);
            
            // Merge results, removing duplicates based on team ID
            const teamIds = new Set(allTeams.map(t => t.id));
            const newTeams = additionalTeams.filter(team => !teamIds.has(team.id));
            
            log.info(`Original teams: ${allTeams.length}, Additional teams: ${additionalTeams.length}, New unique teams: ${newTeams.length}`);
            
            allTeams = [...allTeams, ...newTeams];
            
            log.info(`Total teams after merging: ${allTeams.length}`);
          } catch (err) {
            log.warn('Additional searches failed:', err);
            // Continue with original results
          }
        }
      }

      // Client-side filtering and scoring
      let scoredResults = allTeams.map(team => {
        const teamNameLower = (team.name || '').toLowerCase();
        const teamDesc = (team.description || '').toLowerCase();
        const teamSport = (team.sport_activity || '').toLowerCase();
        const teamState = (team.state || '').toLowerCase();
        const teamDivision = (team.division || '').toLowerCase();
        
        let score = 0;
        let matches = true;
        
        if (searchWords.length === 1) {
          // Single word search - already filtered by database
          matches = true;
          score = teamNameLower.includes(searchWords[0]) ? 100 : 50;
        } else {
          // Multi-word search - check if all words appear somewhere
          const allText = `${teamNameLower} ${teamDesc} ${teamSport} ${teamState} ${teamDivision}`;
          
          // Check if all words exist in the team data
          matches = searchWords.every(word => allText.includes(word));
          
          if (matches) {
            // Score based on where and how the words appear
            // Highest score for exact phrase match in name
            if (teamNameLower.includes(searchQuery.toLowerCase())) {
              score = 200;
            }
            // High score if all words are in the name
            else if (searchWords.every(word => teamNameLower.includes(word))) {
              score = 150;
              // Bonus for words appearing in order
              const wordsInOrder = searchWords.join('.*');
              if (new RegExp(wordsInOrder).test(teamNameLower)) {
                score = 175;
              }
            }
            // Medium score if some words in name, others elsewhere
            else if (searchWords.some(word => teamNameLower.includes(word))) {
              score = 100;
            }
            // Lower score if words only appear in other fields
            else {
              score = 50;
            }
          }
        }
        
        return { ...team, searchScore: score, matches };
      });
      
      // Filter to only matching teams and sort by score
      let filteredResults = scoredResults
        .filter(team => team.matches)
        .sort((a, b) => {
          // Sort by score first, then alphabetically
          if (a.searchScore !== b.searchScore) {
            return b.searchScore - a.searchScore;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

      log.info(`Teams after filtering and scoring: ${filteredResults.length}`);
      
      // Filter out teams where excludeUserId is already a manager (if specified)
      if (excludeUserId && includeUserRoles) {
        filteredResults = filteredResults.filter(team => 
          !team.user_team_roles?.some(role => role.user_id === excludeUserId)
        );
      }

      // Final deduplication to prevent duplicate keys
      const finalTeamIds = new Set();
      filteredResults = filteredResults.filter(team => {
        if (finalTeamIds.has(team.id)) {
          log.warn(`Removing duplicate team: ${team.name} (ID: ${team.id})`);
          return false;
        }
        finalTeamIds.add(team.id);
        return true;
      });

      // Limit final results
      const finalResults = filteredResults.slice(0, maxResults);

      log.info(`Final teams after deduplication: ${finalResults.length}`);

      return {
        data: finalResults,
        count: finalResults.length,
        totalFound: filteredResults.length,
        searchMeta: {
          query: searchQuery,
          words: searchWords,
          initialResults: teams?.length || 0,
          finalResults: finalResults.length,
          additionalSearches: searchWords.length > 1 && allTeams.length === limit
        }
      };
    },
    { operation: 'Advanced team search' }
  ),

  /**
   * Get pending team manager requests for a user
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getUserPendingRequests: createAPIMethod(
    async (userId) => {
      const { data, error } = await supabase
        .from('pending_team_managers')
        .select(`
          *,
          teams (
            name,
            company,
            sport_activity,
            state,
            division
          )
        `)
        .eq('user_id', userId)
        .order('requested_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    { operation: 'Get user pending requests' }
  ),

  /**
   * Submit team manager access request
   * @param {string} teamId - Team ID
   * @param {string} justification - Request justification
   * @returns {Promise<APIResponse>}
   */
  requestManagerAccess: createAPIMethod(
    async (teamId, justification) => {
      const { data, error } = await supabase.functions.invoke('request-team-manager-access', {
        body: {
          team_id: teamId,
          justification: justification.trim()
        }
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Submit team manager access request' }
  ),

  /**
   * Validate team invite code
   * @param {string} code - Invite code
   * @returns {Promise<APIResponse>}
   */
  validateInviteCode: createAPIMethod(
    async (code) => {
      const { data, error } = await supabase.functions.invoke('redeem-team-invite-code', {
        body: {
          code: code.trim(),
          validate_only: true
        }
      });

      if (error) throw error;
      return data;
    },
    { operation: 'Validate team invite code' }
  ),

  /**
   * Redeem team invite code
   * @param {string} code - Invite code
   * @returns {Promise<APIResponse>}
   */
  redeemInviteCode: createAPIMethod(
    async (code) => {
      try {
        const { data, error } = await supabase.functions.invoke('redeem-team-invite-code', {
          body: {
            code: code.trim(),
            validate_only: false
          }
        });

        if (error) throw error;
        return data;
      } catch (error) {
        // Handle Edge Function errors properly
        // The error.context is a Response object, we need to read its body
        let errorMessage = null;
        
        if (error.context && error.context instanceof Response) {
          try {
            // Clone the response so we can read it
            const response = error.context.clone();
            const responseText = await response.text();
            
            if (responseText) {
              try {
                const errorBody = JSON.parse(responseText);
                if (errorBody.error) {
                  errorMessage = errorBody.error;
                }
              } catch {
                // Response wasn't JSON, use as-is
                errorMessage = responseText;
              }
            }
          } catch (readError) {
            log.error('Error reading error response:', readError);
          }
        }
        
        // If we found an error message, throw it as a proper Error
        if (errorMessage) {
          throw new Error(errorMessage);
        }
        
        // If we can't extract a specific error message, throw the original error
        throw error;
      }
    },
    { operation: 'Redeem team invite code' }
  )
};